import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from '@/stores/authStore'
import { useRecruiterStore } from '@/stores/recruiterStore'
import { ToastContainer } from '@/components/common/ToastContainer'
import EmailVerificationModal from '@/components/common/EmailVerificationModal'
import DataConsentModal from '@/components/common/DataConsentModal'
import RecruiterSubscriptionModal from '@/components/common/RecruiterSubscriptionModal'
import { useDataConsentModal } from '@/stores/dataConsentStore'

// Layout components
import Layout from '@/components/layout/Layout'
import AppLayout from '@/components/layout/AppLayout'
import AuthLayout from '@/components/layout/AuthLayout'

// Public pages
import HomePage from '@/pages/public/HomePage'
import CandidateProfilePage from '@/pages/public/CandidateProfilePage'
import TermsOfServicePage from '@/pages/public/TermsOfServicePage'
import PrivacyPolicyPage from '@/pages/public/PrivacyPolicyPage'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage'
import SignupChoicePage from '@/pages/auth/SignupChoicePage'
import RegisterPage from '@/pages/auth/RegisterPage'
import RecruiterRegisterPage from '@/pages/auth/RecruiterRegisterPage'
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'

// Candidate pages
import CandidateDashboard from '@/pages/candidate/CandidateDashboard'
import CandidateProfileEdit from '@/pages/candidate/CandidateProfileEdit'
import CandidateJobOfferDetails from '@/pages/candidate/CandidateJobOfferDetails'

// Recruiter pages
import RecruiterDashboard from '@/pages/recruiter/RecruiterDashboard'
import RecruiterProfileEdit from '@/pages/recruiter/RecruiterProfileEdit'
import SearchCandidates from '@/pages/recruiter/SearchCandidates'
import Contacts from '@/pages/recruiter/Contacts'
import PacksPage from '@/pages/recruiter/PacksPage'
import PaymentSuccessPage from '@/pages/recruiter/PaymentSuccessPage'
import PaymentCancelPage from '@/pages/recruiter/PaymentCancelPage'
import AIMatching from '@/pages/recruiter/AIMatching'
import CreateAIMatch from '@/pages/recruiter/CreateAIMatch'
import AIMatchResults from '@/pages/recruiter/AIMatchResults'
import AIMatchJobCandidates from '@/pages/recruiter/AIMatchJobCandidates'
import JobOffersManager from '@/pages/recruiter/JobOffersManager'
import JobsPage from '@/pages/recruiter/JobsPage'
import JobDetailPage from '@/pages/recruiter/JobDetailPage'
import JobProspectsPage from '@/pages/recruiter/JobProspectsPage'
import PackHistoryPage from '@/pages/recruiter/PackHistoryPage'
import ReceivedJobOffers from '@/pages/candidate/ReceivedJobOffers'
import SendJobOfferPage from '@/pages/recruiter/SendJobOfferPage'
import EditJobOfferPage from '@/pages/recruiter/EditJobOfferPage'
import SentJobOfferDetailPage from '@/pages/recruiter/SentJobOfferDetailPage'
import CandidateDetailPage from '@/pages/recruiter/CandidateDetailPage'

// Protected route component
function ProtectedRoute({
  children,
  allowedRole,
}: {
  children: React.ReactNode
  allowedRole?: 'CANDIDATE' | 'RECRUITER'
}) {
  const { user, isAuthenticated, _hasHydrated } = useAuth()

  // If we have valid auth state (user and isAuthenticated), proceed even if not hydrated yet
  // This handles the case where user just logged in and state is set programmatically
  const hasValidAuth = isAuthenticated && user !== null

  // Only show loading if:
  // 1. Not hydrated yet AND
  // 2. No valid auth state (meaning we're truly waiting for localStorage to load)
  if (!_hasHydrated && !hasValidAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Requires at least one purchased pack; redirects to /recruiter/packs otherwise.
// Must be used inside a ProtectedRoute with allowedRole="RECRUITER".
function SubscriptionRoute({ children }: { children: React.ReactNode }) {
  const { profile, profileLoaded } = useRecruiterStore()
  if (!profileLoaded) return null
  if (profile === null || profile.recentPacks.length === 0) {
    return <Navigate to="/recruiter/packs" replace />
  }
  return <>{children}</>
}

// Refreshes recruiter profile on every route change so quota/packs stay current.
// Must live inside BrowserRouter to access useLocation.
function RecruiterProfileSync() {
  const { _hasHydrated, user, isAuthenticated } = useAuth()
  const { refreshProfile } = useRecruiterStore()
  const location = useLocation()

  useEffect(() => {
    if (_hasHydrated && isAuthenticated && user?.role === 'RECRUITER') {
      refreshProfile()
    }
  }, [location.pathname, _hasHydrated, isAuthenticated, user?.role, refreshProfile])

  return null
}

function App() {
  const { _hasHydrated, user, isAuthenticated } = useAuth()
  const { showModal: showConsentModal } = useDataConsentModal()

  // Check if user needs to give data consent
  // Only candidates need to give consent (their profiles are shared with recruiters)
  useEffect(() => {
    if (_hasHydrated && isAuthenticated && user) {
      // Only show consent modal for candidates, not recruiters
      if (user.role === 'CANDIDATE' && !user.dataProcessingConsent) {
        showConsentModal()
      }
    }
  }, [_hasHydrated, isAuthenticated, user, showConsentModal])

  return (
    <BrowserRouter>
      <RecruiterProfileSync />
      <ToastContainer />
      <EmailVerificationModal />
      <DataConsentModal />
      <RecruiterSubscriptionModal />
      <Routes>
        {/* Public routes */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
        </Route>

        {/* Legal pages - standalone without navbar/footer */}
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Profile page - standalone without navbar/footer */}
        <Route path="/profile/:slug" element={<CandidateProfilePage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<SignupChoicePage />} />
          <Route path="/register/candidate" element={<RegisterPage />} />
          <Route path="/register/recruiter" element={<RecruiterRegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Candidate routes */}
        <Route element={<AppLayout />}>
          <Route
            path="/candidate/dashboard"
            element={
              <ProtectedRoute allowedRole="CANDIDATE">
                <CandidateDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/job-offers"
            element={
              <ProtectedRoute allowedRole="CANDIDATE">
                <ReceivedJobOffers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/job-offers/:id"
            element={
              <ProtectedRoute allowedRole="CANDIDATE">
                <CandidateJobOfferDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/profile/edit"
            element={
              <ProtectedRoute allowedRole="CANDIDATE">
                <CandidateProfileEdit />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Recruiter routes */}
        <Route element={<AppLayout />}>
          {/* Always accessible to logged-in recruiters (no subscription needed) */}
          <Route
            path="/recruiter/profile/edit"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <RecruiterProfileEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/packs"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PacksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/pack-history"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PackHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/payment/success"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PaymentSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/payment/cancel"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PaymentCancelPage />
              </ProtectedRoute>
            }
          />
          {/* Flouci redirect landing pages — backend redirects here after payment */}
          <Route
            path="/payment/success"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PaymentSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/fail"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <PaymentCancelPage />
              </ProtectedRoute>
            }
          />

          {/* Requires an active subscription */}
          <Route
            path="/recruiter/dashboard"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <RecruiterDashboard />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/search"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <SearchCandidates />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/contacts"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <Contacts />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/recruiter/saved" element={<Navigate to="/recruiter/contacts" replace />} />
          <Route
            path="/recruiter/ai-matching"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <AIMatching />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/ai-matching/create"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <CreateAIMatch />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/ai-matching/results/:matchId"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <AIMatchResults />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/ai-matching/job/:jobId"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <AIMatchJobCandidates />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/job-offers"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <JobOffersManager />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/jobs"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <JobsPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/jobs/:jobId"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <JobDetailPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/jobs/:jobId/prospects"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <JobProspectsPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/templates"
            element={<Navigate to="/recruiter/job-offers" replace />}
          />
          {/* New pages replacing modals */}
          <Route
            path="/recruiter/job-offers/new"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <SendJobOfferPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/job-offers/:id/edit"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <EditJobOfferPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/job-offers/:id"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <SentJobOfferDetailPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter/candidates/:candidateId"
            element={
              <ProtectedRoute allowedRole="RECRUITER">
                <SubscriptionRoute>
                  <CandidateDetailPage />
                </SubscriptionRoute>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
